import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, from } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { ProfileService } from '../../../core/services/profile.service';
import { Auth0Service } from '../../../core/auth/services/auth0.service';
import * as ProfileActions from './profile.actions';
import * as AppInitActions from '../../app-init/app-init.actions';

@Injectable()
export class ProfileEffects {
  private actions$ = inject(Actions);
  private profileService = inject(ProfileService);
  private auth0Service = inject(Auth0Service);

  loadProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadProfile),
      switchMap(() =>
        this.profileService.getProfile().pipe(
          map((profile) => ProfileActions.loadProfileSuccess({ profile })),
          catchError((error) =>
            of(ProfileActions.loadProfileFailure({ error: error.message }))
          )
        )
      )
    )
  );

  markProfileInitialized$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadProfileSuccess),
      map(() => AppInitActions.markRequestComplete({ requestKey: 'profile' }))
    )
  );

  markProfileFailed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadProfileFailure),
      map(() => AppInitActions.markRequestFailed({ requestKey: 'profile' }))
    )
  );

  updateProfile$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.updateProfile),
      switchMap(({ data }) =>
        this.profileService.updateProfile(data).pipe(
          map((profile) => ProfileActions.updateProfileSuccess({ profile })),
          catchError((error) =>
            of(ProfileActions.updateProfileFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadDepartments$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadDepartments),
      switchMap(() =>
        this.profileService.getDepartments().pipe(
          map((departments) =>
            ProfileActions.loadDepartmentsSuccess({ departments })
          ),
          catchError((error) =>
            of(ProfileActions.loadDepartmentsFailure({ error: error.message }))
          )
        )
      )
    )
  );

  loadTimezones$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.loadTimezones),
      switchMap(() =>
        this.profileService.getTimezones().pipe(
          map((timezones) =>
            ProfileActions.loadTimezonesSuccess({ timezones })
          ),
          catchError((error) =>
            of(ProfileActions.loadTimezonesFailure({ error: error.message }))
          )
        )
      )
    )
  );

  regenerateToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.regenerateToken),
      switchMap(() =>
        from(this.auth0Service.getRefreshTokenForRegenerate()).pipe(
          switchMap((refreshToken) =>
            this.profileService.regenerateToken(refreshToken).pipe(
              map(({ refreshToken }) =>
                ProfileActions.regenerateTokenSuccess({ refreshToken })
              ),
              catchError((error) =>
                of(ProfileActions.regenerateTokenFailure({ error: error.message }))
              )
            )
          ),
          catchError((error) =>
            of(ProfileActions.regenerateTokenFailure({ error: error.message }))
          )
        )
      )
    )
  );

  uploadPhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.uploadPhoto),
      switchMap(({ file }) =>
        this.profileService.uploadPhoto(file).pipe(
          map(({ photo }) => ProfileActions.uploadPhotoSuccess({ photo })),
          catchError((error) =>
            of(ProfileActions.uploadPhotoFailure({ error: error.message }))
          )
        )
      )
    )
  );

  deletePhoto$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProfileActions.deletePhoto),
      switchMap(() =>
        this.profileService.deletePhoto().pipe(
          map(() => ProfileActions.deletePhotoSuccess()),
          catchError((error) =>
            of(ProfileActions.deletePhotoFailure({ error: error.message }))
          )
        )
      )
    )
  );
}
